import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import dayjs from 'dayjs/esm';
import utc from 'dayjs/esm/plugin/utc'

dayjs.extend(utc)
import { isPresent } from 'app/core/util/operators';
import { ApplicationConfigService } from 'app/core/config/application-config.service';
import { createRequestOption } from 'app/core/request/request-util';
import { ITicket, NewTicket } from '../ticket.model';
import { TicketRevenueStats } from 'app/dashboard/model/dashboard.model';
import { TicketAuditDTO } from "../ticket-audit.model";

export type PartialUpdateTicket = Partial<ITicket> & Pick<ITicket, 'id' | 'images'>;

type RestOf<T extends ITicket | NewTicket> = Omit<T, 'arrivalDate' | 'departureDate'> & {
  arrivalDate?: string | null;
  departureDate?: string | null;
};

export type RestTicket = RestOf<ITicket>;

export type NewRestTicket = RestOf<NewTicket>;

export type PartialUpdateRestTicket = RestOf<PartialUpdateTicket>;

export type EntityResponseType = HttpResponse<ITicket>;
export type EntityArrayResponseType = HttpResponse<ITicket[]>;

@Injectable({ providedIn: 'root' })
export class TicketService {
  protected http = inject(HttpClient);
  protected applicationConfigService = inject(ApplicationConfigService);

  protected resourceUrl = this.applicationConfigService.getEndpointFor('api/tickets');
  protected resourceUrlExport = this.applicationConfigService.getEndpointFor('api/export');

  create(ticket: NewTicket, imageMap: Map<string, File[]>, imageToDel: number[]): Observable<EntityResponseType> {
    const formData = new FormData();
    const ticketData = this.convertDateFromClient(ticket);
    this.appendFormData(formData, ticketData, null);

    // Add files to the form data
    // files.forEach(file => formData.append('files', file));
    imageMap.forEach((files, key) => {
      files.forEach(file => formData.append(key, file));
    });

    return this.http
      .post<RestTicket>(this.resourceUrl, formData, { observe: 'response' })
      .pipe(map(res => this.convertResponseFromServer(res)));
  }

  appendFormData(formData: FormData, data: any, parentKey: string | null = null) {
    if (data && typeof data === 'object' && !(data instanceof File)) {
      if (Array.isArray(data)) {
        data.forEach((item, index) => {
          const fullKey = parentKey ? `${parentKey}[${index}]` : `${index}`;
          this.appendFormData(formData, item, fullKey);
        });
      } else {
        Object.keys(data).forEach(key => {
          const value = data[key];
          if (value !== null) {
            const fullKey = parentKey ? `${parentKey}.${key}` : key;
            this.appendFormData(formData, data[key], fullKey);
          }
        });
      }
    } else {
      if (parentKey && data !== null) {
        formData.append(parentKey, data);
      }
    }
  }

  update(ticket: ITicket, imageMap: Map<string, File[]>, imageToDel: number[]): Observable<EntityResponseType> {
    const formData = new FormData();
    const ticketData = this.convertDateFromClient(ticket);
    this.appendFormData(formData, ticketData, null);

    // Add files to the form data
    // files.forEach(file => formData.append('files', file));
    imageMap.forEach((files, key) => {
      files.forEach(file => formData.append(key, file));
    });

    // Add image ids to delete to the form data
    imageToDel.forEach(id => formData.append('imgToDel', id.toString()));

    return this.http
      .put<RestTicket>(`${this.resourceUrl}/${this.getTicketIdentifier(ticket)}`, formData, { observe: 'response' })
      .pipe(map(res => this.convertResponseFromServer(res)));
  }

  private createFormData(ticket: ITicket | NewTicket, files: File[]): FormData {
    const formData = new FormData();
    formData.append('ticket', new Blob([JSON.stringify(this.convertDateFromClient(ticket))], { type: 'application/json' }));
    files.forEach(file => formData.append('files', file));
    return formData;
  }

  partialUpdate(ticket: PartialUpdateTicket, type: string): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(ticket);
    return this.http
      .patch<RestTicket>(`${this.resourceUrl}/${this.getTicketIdentifier(ticket)}`, copy, { params: { type: type }, observe: 'response' })
      .pipe(map(res => this.convertResponseFromServer(res)));
  }

  deliveryTicket(id: number | undefined): Observable<any> {
    return this.http
      .post<any>(`${this.resourceUrl}/delivered/${id}`, { observe: 'response' });
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http
      .get<RestTicket>(`${this.resourceUrl}/${id}`, { observe: 'response' })
      .pipe(map(res => this.convertResponseFromServer(res)));
  }

  getAuditHistory(ticketId: number | undefined): Observable<TicketAuditDTO[]> {
    return this.http.get<TicketAuditDTO[]>(`${this.resourceUrl}/history/${ticketId}`);
  }

  getTicketByCode(code: string): Observable<EntityResponseType> {
    return this.http
      .get<RestTicket>(`${this.resourceUrl}/request/${code}`, { observe: 'response' })
      .pipe(map(res => this.convertResponseFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<RestTicket[]>(this.resourceUrl, { params: options, observe: 'response' })
      .pipe(map(res => this.convertResponseArrayFromServer(res)));
  }

  getTicketRequestedByGuest(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<RestTicket[]>(`${this.resourceUrl}/getTicketRequestedByGuest`, { observe: 'response' })
      .pipe(map(res => this.convertResponseArrayFromServer(res)));
  }

  getNumberOfRequestedCar(): Observable<number> {
    return this.http
      .get<number>(`${this.resourceUrl}/numberOfRequestedCars`, { observe: 'body' });
  }

  delete(id: number, status: string | null = null): Observable<HttpResponse<{}>> {
    const options = createRequestOption({ status: status });
    return this.http.delete(`${this.resourceUrl}/${id}`, { params: options, observe: 'response' });
  }

  requestCar(id: number | undefined): Observable<any> {
    return this.http
      .post(`${this.resourceUrl}/request/${id}`,
        { observe: 'response' });
  }

  exportToCsv(req?: any): Observable<HttpResponse<Blob>> {
    const options = createRequestOption(req);
    return this.http
      .get<Blob>(`${this.resourceUrlExport}`, { params: options, observe: 'response', responseType: 'blob' as 'json' })
  }

  getTicketIdentifier(ticket: Pick<ITicket, 'id'>): number {
    return ticket.id;
  }

  compareTicket(o1: Pick<ITicket, 'id'> | null, o2: Pick<ITicket, 'id'> | null): boolean {
    return o1 && o2 ? this.getTicketIdentifier(o1) === this.getTicketIdentifier(o2) : o1 === o2;
  }

  addTicketToCollectionIfMissing<Type extends Pick<ITicket, 'id'>>(
    ticketCollection: Type[],
    ...ticketsToCheck: (Type | null | undefined)[]
  ): Type[] {
    const tickets: Type[] = ticketsToCheck.filter(isPresent);
    if (tickets.length > 0) {
      const ticketCollectionIdentifiers = ticketCollection.map(ticketItem => this.getTicketIdentifier(ticketItem));
      const ticketsToAdd = tickets.filter(ticketItem => {
        const ticketIdentifier = this.getTicketIdentifier(ticketItem);
        if (ticketCollectionIdentifiers.includes(ticketIdentifier)) {
          return false;
        }
        ticketCollectionIdentifiers.push(ticketIdentifier);
        return true;
      });
      return [...ticketsToAdd, ...ticketCollection];
    }
    return ticketCollection;
  }

  getTicketRevenue(req?: any): Observable<HttpResponse<TicketRevenueStats>> {
    const options = createRequestOption(req);
    return this.http.get<TicketRevenueStats>(`${this.resourceUrl}/revenue`, { params: options, observe: 'response' });
  }

  protected convertDateFromClient<T extends ITicket | NewTicket | PartialUpdateTicket>(ticket: T): RestOf<T> {
    return {
      ...ticket,
      arrivalDate: ticket.arrivalDate ? `${ticket.arrivalDate.format('YYYY-MM-DDTHH:mm:ss.SSS')}Z` : null,
      departureDate: ticket.departureDate ? `${ticket.departureDate.format('YYYY-MM-DDTHH:mm:ss.SSS')}Z` : null,
      // arrivalDate: ticket.arrivalDate ? ticket.arrivalDate.format('YYYY-MM-DDTHH:mm') : null,
      // departureDate: ticket.departureDate ? ticket.departureDate.format('YYYY-MM-DDTHH:mm') : null,
    };
  }

  protected convertDateFromServer(restTicket: RestTicket): ITicket {
    return {
      ...restTicket,
      // arrivalDate: restTicket.arrivalDate ? dayjs(restTicket.arrivalDate) : undefined,
      // departureDate: restTicket.departureDate ? dayjs(restTicket.departureDate)  : undefined,
      arrivalDate: restTicket.arrivalDate ? dayjs.utc(restTicket.arrivalDate) : undefined,
      departureDate: restTicket.departureDate ? dayjs.utc(restTicket.departureDate) : undefined,
    };
  }

  protected convertResponseFromServer(res: HttpResponse<RestTicket>): HttpResponse<ITicket> {
    return res.clone({
      body: res.body ? this.convertDateFromServer(res.body) : null,
    });
  }

  protected convertResponseArrayFromServer(res: HttpResponse<RestTicket[]>): HttpResponse<ITicket[]> {
    return res.clone({
      body: res.body ? res.body.map(item => this.convertDateFromServer(item)) : null,
    });
  }
}
